import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * PasswordInput — reusable password field with show/hide toggle.
 * Preserves all Input props (value, onChange, placeholder, data-testid, etc.).
 * The toggle button has its own data-testid derived from the input's testid.
 */
const PasswordInput = React.forwardRef(({ className, "data-testid": testId, ...props }, ref) => {
  const [visible, setVisible] = React.useState(false);
  const toggleId = testId ? `${testId}-toggle` : "password-toggle";
  return (
    <div className="relative">
      <input
        {...props}
        ref={ref}
        type={visible ? "text" : "password"}
        data-testid={testId}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-10 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
        aria-pressed={visible}
        tabIndex={-1}
        data-testid={toggleId}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[#6B6B6B] hover:text-[#0A0A0A] hover:bg-[#F5F5F1] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DC2626]/40"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
});
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
